import React, { useState } from 'react';
import { LinearProgress, Box, Select, Link, Typography } from '@material-ui/core';
import { ResponseErrorPanel, Table, SubvalueCell } from '@backstage/core-components';
import { PipelineStageStatus } from '../PipelineStageStatus/PipelineStageStatus.esm.js';
import { formatTime } from '../../util/time.esm.js';
import { usePipelineExecutions } from '../../hooks/usePipelineExecutions.esm.js';
import 'react-use';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import '../../api/AwsCodePipelineApi.esm.js';
import { MissingResources } from '@aws/aws-core-plugin-for-backstage-react';
import '@backstage/catalog-model';
import { generateShortcutLink } from '@aws/aws-core-plugin-for-backstage-common';
import { parse } from '@aws-sdk/util-arn-parser';

const renderTrigger = (row) => {
  if (row.sourceRevisions === void 0) {
    return /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, "-");
  }
  let commitMessage = "";
  if (row.sourceRevisions.length > 0) {
    const sourceRevision = row.sourceRevisions[0];
    if (sourceRevision.revisionSummary) {
      switch (sourceRevision.actionName) {
        case "SourceAction":
          commitMessage = sourceRevision.revisionSummary || "";
          break;
        case "Source":
          commitMessage = sourceRevision.revisionSummary || "";
          break;
        case "Checkout": {
          const summary = JSON.parse(sourceRevision.revisionSummary || "{}");
          commitMessage = summary.CommitMessage;
          break;
        }
      }
    }
    const subvalue = /* @__PURE__ */ React.createElement(React.Fragment, null, sourceRevision.revisionId?.substring(0, 6) || "", " - ", commitMessage);
    return /* @__PURE__ */ React.createElement(SubvalueCell, { value: sourceRevision.actionName, subvalue });
  }
  return "-";
};
const generatedColumns = (pipelineName, region, accountId, ssoSubdomain) => {
  return [
    {
      title: "Execution",
      field: "Pipeline",
      render: (row) => {
        if (row.pipelineExecutionId) {
          const projectUrl = `https://${region}.console.aws.amazon.com/codesuite/codepipeline/pipelines/${pipelineName}/executions/${row.pipelineExecutionId}/timeline?region=${region}`;
          return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
            Link,
            {
              href: ssoSubdomain ? generateShortcutLink(ssoSubdomain, accountId, projectUrl) : projectUrl,
              target: "_blank"
            },
            row.pipelineExecutionId
          ));
        }
        return /* @__PURE__ */ React.createElement(React.Fragment, null);
      }
    },
    {
      title: "Last Run",
      field: "",
      render: (row) => `${formatTime(row.lastUpdateTime)} ago`
    },
    {
      title: "Status",
      field: "status",
      render: (row) => {
        if (row.status) {
          return /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center" }, /* @__PURE__ */ React.createElement(PipelineStageStatus, { status: row?.status }));
        }
        return /* @__PURE__ */ React.createElement(React.Fragment, null);
      }
    },
    {
      title: "Source Revision",
      field: "revisions",
      render: (row) => renderTrigger(row)
    }
  ];
};
const CodePipelineExecutionsTable = ({
  response
}) => {
  const configApi = useApi(configApiRef);
  const ssoSubdomain = configApi.getOptionalString("aws.sso.subdomain");
  const { accountId } = parse(response.pipelineArn);
  return /* @__PURE__ */ React.createElement(
    Table,
    {
      data: response.pipelineExecutions,
      columns: generatedColumns(
        response.pipelineName,
        response.pipelineRegion,
        accountId,
        ssoSubdomain
      ),
      title: "AWS CodePipeline"
    }
  );
};
const CodePipelineMultipleExecutionsContent = ({
  response
}) => {
  const [selected, setSelected] = useState(
    response.pipelineExecutions[0]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { marginBottom: 2 }, /* @__PURE__ */ React.createElement(
    Select,
    {
      "data-testid": "select-pipeline",
      value: selected.pipelineArn,
      onChange: (e) => setSelected(
        response.pipelineExecutions.find(
          (p) => p.pipelineArn === e.target.value
        )
      )
    },
    response.pipelineExecutions.map((execution) => /* @__PURE__ */ React.createElement("option", { key: execution.pipelineArn, value: execution.pipelineArn }, execution.pipelineName))
  )), /* @__PURE__ */ React.createElement(CodePipelineExecutionsTable, { response: selected }));
};
const CodePipelineExecutionsContent = ({
  response
}) => {
  if (response?.pipelineExecutions.length === 0) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MissingResources, null));
  }
  if (response.pipelineExecutions.length === 1) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      CodePipelineExecutionsTable,
      {
        response: response.pipelineExecutions[0]
      }
    ));
  }
  return /* @__PURE__ */ React.createElement(CodePipelineMultipleExecutionsContent, { response });
};
const CodePipelineExecutions = ({
  entity
}) => {
  const { response, loading, error } = usePipelineExecutions({ entity });
  if (error) {
    return /* @__PURE__ */ React.createElement(ResponseErrorPanel, { error });
  }
  if (loading) {
    return /* @__PURE__ */ React.createElement(LinearProgress, null);
  }
  return /* @__PURE__ */ React.createElement(CodePipelineExecutionsContent, { response });
};

export { CodePipelineExecutions };
//# sourceMappingURL=CodePipelineExecutions.esm.js.map
